/* eslint-disable react/prop-types */

import { useState, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import { useTranslation } from "react-i18next";

import Modal from "@mui/material/Modal";
import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiDropzone from "components/SuiDropzone";
import SuiTypography from "components/SuiTypography";

import Card from "@mui/material/Card";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import DataTable from "examples/Tables/DataTable";

import { Media } from "api";

let selectedFiles = [];

function MediaModal({ open, onClose }) {
  const { t } = useTranslation();
  const dzRef = useRef(null);
  const [isOpened, setIsOpened] = useState(open);
  const [activeTab, setActiveTab] = useState(1);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState({});
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const handleClose = () => {
    setIsOpened(false);
    if (typeof onClose === "function") {
      onClose({ selectedImage });
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const {
          data: { results },
        } = await Media.getAll({ pageSize: 1000 });
        setImages(results);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [isUploading, uploadResults]);

  useEffect(() => {
    selectedFiles = [];
    setIsUploading(false);
    setUploadResults({});
  }, [isOpened]);

  const uploadFile = (file) => {
    const n = uploadResults;
    const promise = Media.upload(file, file.size);
    n[file.upload.uuid] = { file, promise };
    setUploadResults(n);
    promise
      .then((result) => {
        if (result) {
          uploadResults[file.upload.uuid] = { file, promise, result };
        } else {
          uploadResults[file.upload.uuid] = { file, promise, error: true };
        }
        setUploadResults(_.clone(uploadResults, true));
      })
      .catch((err) => {
        uploadResults[file.upload.uuid] = { file, promise, error: err };
        setUploadResults(_.clone(uploadResults, true));
      });
    return promise;
  };

  const dataTableData = useMemo(() => {
    const result = {
      columns: [
        {
          Header: t("IMAGE"),
          accessor: "image",
          width: "1%",
          Cell: ({ value }) => (
            <img
              alt={value}
              src={value}
              style={{
                maxWidth: "100px",
                maxHeight: "100px",
              }}
            />
          ),
        },
        { Header: t("FILE NAME"), accessor: "filename" },
        { Header: t("STATUS"), accessor: "status" },
      ],
      rows: [],
    };
    Object.entries(uploadResults).forEach(([, uploadResult]) => {
      const isSuccess = !!uploadResult.result;
      const isError = !!uploadResult.error;
      let color = "text";
      let status = "Uploading";

      if (isSuccess) {
        color = "success";
        status = t("Uploaded");
      } else if (isError) {
        color = "error";
        status = t("Failed");
      }

      result.rows.push({
        image: _.get(uploadResult, "file.dataURL", ""),
        filename: _.get(uploadResult, "file.name", ""),
        status: (
          <SuiTypography variant="caption" color={color}>
            {status}
          </SuiTypography>
        ),
      });
    });
    return result;
  }, [uploadResults]);

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <SuiBox
        style={{
          position: "absolute",
          top: "25%",
          left: "50%",
          transform: "translate(-50%, -25%)",
          width: "70%",
          height: "70%",
        }}
      >
        <SuiBox>
          <Card>
            <SuiBox lineHeight={1}>
              <SuiBox display="flex" justifyContent="center" alignItems="center" pt={2}>
                <SuiBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  width="30%"
                >
                  <SuiBox width="100%">
                    <AppBar position="static">
                      <Tabs
                        orientation="horizontal"
                        value={activeTab}
                        onChange={(...args) => {
                          setActiveTab(args[1]);
                        }}
                      >
                        <Tab label={t("Upload")} style={{ padding: 10 }} />
                        <Tab label={t("Library")} />
                      </Tabs>
                    </AppBar>
                  </SuiBox>
                </SuiBox>
                <SuiBox />
              </SuiBox>
            </SuiBox>
            <Divider />
            <SuiBox>
              <SuiBox mb={1} ml={3}>
                {activeTab === 0 && (
                  <SuiButton
                    variant="gradient"
                    color="info"
                    disabled={isUploading}
                    onClick={async () => {
                      if (selectedFiles.length) {
                        const accepteds = dzRef.current.getAcceptedFiles();
                        const uploads = {};
                        accepteds.forEach((file) => {
                          uploads[file.upload.uuid] = { file };
                          uploadFile(file);
                        });
                        setIsUploading(true);
                        setUploadResults(uploads);
                      }
                    }}
                  >
                    {t("Upload")}
                  </SuiButton>
                )}
                {activeTab === 1 && (
                  <SuiButton
                    variant="gradient"
                    color="info"
                    disabled={!selectedImage}
                    onClick={handleClose}
                  >
                    {t("Select")}
                  </SuiButton>
                )}
              </SuiBox>
              <SuiBox
                style={{
                  display: activeTab === 0 ? null : "none",
                }}
              >
                {!isUploading ? (
                  <SuiBox p={3}>
                    <SuiBox>
                      <Grid container>
                        <Grid item xs={12}>
                          <SuiDropzone
                            ref={dzRef}
                            options={{
                              autoProcessQueue: false,
                              accept: (file, done) => {
                                if (file.type.indexOf("image") !== 0) {
                                  done("File type not accepted.");
                                } else if (file.size > 1024 * 1024 * 10) {
                                  // 10MB
                                  done("File size too large.");
                                } else {
                                  done();
                                }
                              },
                              addedfiles: (files) => {
                                selectedFiles = files;
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </SuiBox>
                  </SuiBox>
                ) : (
                  <>
                    <Grid item>
                      <DataTable
                        table={dataTableData}
                        entriesPerPage={{
                          defaultValue: 9999,
                        }}
                        showTotalEntries={false}
                        isSorted={false}
                        showEntriesPerPageCaption={false}
                      />
                    </Grid>
                  </>
                )}
              </SuiBox>

              {activeTab === 1 && (
                <SuiBox my={1.5}>
                  <Grid
                    container
                    style={{
                      maxHeight: 600,
                      overflow: "auto",
                    }}
                  >
                    <Grid item xs={12}>
                      <Card>
                        <SuiBox p={3}>
                          <SuiBox>
                            <Grid container>
                              {images.map((img) => (
                                <Grid item xs={12} md={6} lg={3} mb={3} pr={3} key={img.id}>
                                  <SuiBox
                                    p={3}
                                    style={{
                                      cursor: "pointer",
                                      borderRadius: "15px",
                                      borderWidth: 2,
                                      borderColor:
                                        _.get(selectedImage, "id") === img.id ? "#2152ff" : "#eee",
                                      borderStyle: "solid",
                                    }}
                                    onClick={() => {
                                      setSelectedImage(
                                        _.get(selectedImage, "id") === img.id ? null : img
                                      );
                                    }}
                                  >
                                    <SuiBox display="flex" justifyContent="center" mb={3}>
                                      <img
                                        alt=""
                                        src={img.s3ObjKey.Location}
                                        style={{
                                          maxWidth: "100%",
                                          maxHeight: 100,
                                        }}
                                      />
                                    </SuiBox>
                                  </SuiBox>
                                </Grid>
                              ))}
                            </Grid>
                          </SuiBox>
                        </SuiBox>
                      </Card>
                    </Grid>
                  </Grid>
                </SuiBox>
              )}
            </SuiBox>
          </Card>
        </SuiBox>
      </SuiBox>
    </Modal>
  );
}

MediaModal.defaultProps = {
  open: false,
  onClose: () => {},
};

MediaModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

export default MediaModal;
